import { useState, useRef, useCallback, useEffect } from "react";
import { scrollToSection } from "@/lib/scroll";
import { CheckCircle, Star, PhoneCall } from "lucide-react";
import { Button } from "@/components/v2/ui/button";
import MandalaWatermark from "./MandalaWatermark";
import InstaBookWidget from "./InstaBookWidget";
import Image from "next/image";
import { FestivalHeroBadge, FestivalHeroDecor } from "@/components/festival/FestivalHeroDecor";

const LANG_MAP = {
  Marwari: "Hindi",
};

// Set to true to bring back the North/South Indian language cards in the hero
const SHOW_STYLE_CARDS = false;

// Rotating "<language> <pandit>" line under the headline, in native script
const NATIVE_PANDITS = [
  { lang: "Hindi", text: "हिंदी पंडित" },
  { lang: "Kannada", text: "ಕನ್ನಡ ಪುರೋಹಿತರು" },
  { lang: "Telugu", text: "తెలుగు పురోహితులు" },
  { lang: "Tamil", text: "தமிழ் புரோகிதர்" },
  { lang: "Malayalam", text: "മലയാളം പൂജാരി" },
  { lang: "Marathi", text: "मराठी गुरुजी" },
  { lang: "Gujarati", text: "ગુજરાતી મહારાજ" },
  { lang: "Bengali", text: "বাংলা পুরোহিত" },
  { lang: "Odia", text: "ଓଡ଼ିଆ ପଣ୍ଡିତ" },
  { lang: "Marwari", text: "मारवाड़ी पंडित" },
];
const LANG_ROTATE_MS = 1500;

const RotatingPanditLanguage = ({ onSelect }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % NATIVE_PANDITS.length),
      LANG_ROTATE_MS
    );
    return () => clearInterval(id);
  }, []);

  const current = NATIVE_PANDITS[index];

  return (
    <button
      type="button"
      onClick={() => onSelect(current.lang)}
      aria-label="Pandits available in all major Indian languages"
      className="inline-flex items-center min-h-[44px] text-2xl md:text-3xl font-semibold text-primary"
    >
      <span key={current.lang} aria-hidden="true" className="inline-block animate-lang-swap">
        {current.text}
      </span>
    </button>
  );
};

const HeroSection = ({ heroData }) => {

  const [highlightInstaBook, setHighlightInstaBook] = useState(false);
  const instaBookRef = useRef(null);

  const scrollToBooking = useCallback((lang) => {
    const mapped = lang ? LANG_MAP[lang] || lang : undefined;
    if (mapped) {
      window.dispatchEvent(
        new CustomEvent("select-language", { detail: mapped })
      );
    }
    // Only scroll on mobile/tablet (below lg breakpoint)
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      scrollToSection("self-booking");
    }
  }, []);

  const redirectToListing = useCallback(() => {
    let lang = "hindi";
    let loc = "mumbai";

    // Attempt to get language/location from document.cookie safely on client side
    if (typeof document !== "undefined") {
      const getCookieValue = (name) => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
      };

      const storedLang = getCookieValue("language_name");
      const storedLoc = getCookieValue("location_name");

      if (storedLang) lang = decodeURIComponent(storedLang);
      if (storedLoc) loc = decodeURIComponent(storedLoc);
    }

    window.location.href = `/${lang.toLowerCase()}/${loc.toLowerCase()}/pooja`;
  }, []);

  const handleDesktopBook = useCallback(() => {
    setHighlightInstaBook(true);
    instaBookRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setTimeout(() => setHighlightInstaBook(false), 8000); // Extended to accommodate slow animation
  }, []);

  const handleMobileBook = useCallback(() => {
    scrollToSection("self-booking");
  }, []);

  const scrollToEnquiry = useCallback(() => {
    scrollToSection("enquiry");
  }, []);

  const northLangs = heroData?.styles?.find(s => s.label === "North Indian")?.langs || [
    "Hindi",
    "Marathi",
    "Gujarati",
    "Bengali",
    "Odia",
    "Marwari",
  ];
  const southLangs = heroData?.styles?.find(s => s.label === "South Indian")?.langs || ["Kannada", "Telugu", "Tamil", "Malayalam"];

  const headline = heroData?.headline || "Worried About Doing the Puja Right?";
  const headlineHighlight = heroData?.headlineHighlight || "Let Us Handle Everything";
  const subheadline = heroData?.subheadline || "Book experienced pandits for personalized home pujas across major Indian cities and online personalized pujas for overseas Indians.";

  return (
    <section className="top-20 md:top-10 relative overflow-hidden bg-gradient-to-br from-[hsl(40,40%,96%)] via-background to-[hsl(355,30%,96%)]">
      {/* Top Bar / Seasonal Banner */}
      {/*<div className="bg-primary text-primary-foreground py-2 text-center text-sm font-medium tracking-wide">*/}
      {/*  <div className="container flex items-center justify-center gap-2">*/}
      {/*    {banner?.isActive ? (*/}
      {/*       <a href={banner.linkUrl} className="flex items-center gap-2 hover:underline">*/}
      {/*         <Star className="h-4 w-4 fill-gold text-gold" />*/}
      {/*         <span>{banner.text} · {banner.linkText}</span>*/}
      {/*         <Star className="h-4 w-4 fill-gold text-gold" />*/}
      {/*       </a>*/}
      {/*    ) : (*/}
      {/*      <>*/}
      {/*        <Star className="h-4 w-4 fill-gold text-gold" />*/}
      {/*        <span>4.8★ Google Rated · Trusted by 10,000+ Families Across India</span>*/}
      {/*        <Star className="h-4 w-4 fill-gold text-gold" />*/}
      {/*      </>*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*</div>*/}

      {/* Festival decorations — renders nothing when no campaign is live */}
      <FestivalHeroDecor />

      {/* Hero Content */}
      <div className="container py-12 md:py-20 pt-8 relative">
        <MandalaWatermark className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] text-primary opacity-[0.07] pointer-events-none" />

        <div className="grid lg:grid-cols-2 gap-10 items-center relative z-10">
          <div className="space-y-6">
            <FestivalHeroBadge />
            <div className="inline-flex items-center gap-2 bg-gold/15 text-black hover:text-gold px-4 py-1.5 rounded-full text-sm font-semibold border border-gold/30 font-sans">
              <Star className="h-3.5 w-3.5 fill-gold text-gold" />
              {heroData?.trustBadge || "Trusted by 10,000+ families"}
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-[3.4rem] leading-tight tracking-tight">
              {headline}{" "}
              <span className="text-primary">{headlineHighlight}</span>{" "}
              – the <span className="text-saffron italic">Right Way.</span>
            </h1>

            <RotatingPanditLanguage onSelect={scrollToBooking} />

            <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-xl">
              {subheadline}
            </p>

            <div className="space-y-3">
              {(heroData?.features || [
                "Certified & Experienced Pandits (All Languages)",
                "Personalised Home Pujas Across Major Indian Cities",
                "Online Personalised Pujas for Overseas Indians",
              ]).map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2.5 text-muted-foreground"
                >
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="font-medium font-sans">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <Button
                size="lg"
                className="text-base px-8 h-13 min-h-[52px] font-sans lg:hidden"
                onClick={handleMobileBook}
              >
                {heroData?.cta?.book || "Book Your Puja →"}
              </Button>
              <Button
                size="lg"
                className="text-base px-8 h-13 min-h-[52px] font-sans hidden lg:inline-flex"
                onClick={handleDesktopBook}
              >
                {heroData?.cta?.book || "Book Your Puja →"}
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="text-black px-8 h-13 min-h-[52px] border-2 border-gold/60  hover:bg-gold/10 font-sans hover:text-BluntRed"
                onClick={scrollToEnquiry}
              >
                <PhoneCall className="h-5 w-5 mr-1" />
                {heroData?.cta?.callback || "Request a Callback"}
              </Button>
            </div>
          </div>

          {/* Right Side — Desktop: Language Cards + InstaBook; Mobile: Language Cards only */}
          <div>
            <div className={`hidden lg:grid gap-4 items-start ${SHOW_STYLE_CARDS ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
              {SHOW_STYLE_CARDS && (
              <div className="space-y-4">
                {(heroData?.styles || [
                  {
                    label: "South Indian",
                    langs: southLangs,
                    img: "/images/hero_banner_2.jpg",
                  },
                  {
                    label: "North Indian",
                    langs: northLangs,
                    img: "/images/hero_banner.jpg",
                  },
                ]).map((style) => (
                  <div
                    key={style.label}
                    className="rounded-xl overflow-hidden border border-primary/10 bg-card shadow-lg"
                  >
                    <div className="h-28 relative">
                      <Image
                        src={style.img}
                        alt={`Authentic ${style.label} Pooja Ceremony`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, 400px"
                        priority
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <span className="absolute bottom-2 left-3 text-white font-bold text-base font-sans">
                        {style.label}
                      </span>
                    </div>
                    <div className="p-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {style.langs.map((lang) => (
                          <button
                            key={lang}
                            aria-label={`Select ${lang} language`}
                            onClick={() => scrollToBooking(lang)}
                            className="text-xs font-semibold px-4 py-2.5 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 font-sans min-w-[60px] min-h-[36px]"
                          >
                            {lang}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}

              <div
                id="instabook"
                ref={instaBookRef}
                className={`transition-all duration-[5000ms] rounded-2xl scroll-m-24 ${highlightInstaBook
                  ? "ring-4 ring-gold/60 shadow-[0_0_30px_hsl(var(--gold)/0.4)] scale-[1.02] animate-attention-pulse"
                  : ""
                  }`}
              >
                <InstaBookWidget />
              </div>
            </div>

            {/* Mobile layout */}
            {SHOW_STYLE_CARDS && (
            <div className="grid grid-cols-2 gap-4 lg:hidden">
              {(heroData?.styles || [
                { label: "South Indian", langs: southLangs, img: "/images/hero_banner_2.jpg" },
                { label: "North Indian", langs: northLangs, img: "/images/hero_banner.jpg" },
              ]).map((style) => (
                <div
                  key={style.label}
                  className="rounded-xl overflow-hidden border border-primary/10 bg-card shadow-lg"
                >
                  <div className="h-36 md:h-44 relative">
                    <Image
                      src={style.img}
                      alt={`Authentic ${style.label} Pooja Ceremony`}
                      fill
                      className="object-cover"
                      sizes="50vw"
                      priority
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className="absolute bottom-3 left-3 text-white font-bold text-lg font-sans">
                      {style.label}
                    </span>
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {style.langs.map((lang) => (
                        <button
                          key={lang}
                          aria-label={`Select ${lang} language`}
                          onClick={() => scrollToBooking(lang)}
                          className="text-xs font-semibold px-4 py-2.5 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 font-sans min-w-[60px] min-h-[36px]"
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
